import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { IconButton } from '../IconButton/IconButton';
import Paragraph from '../Paragraph/Paragraph';
import {
  IRA_ACCOUNT_TYPES,
  IRA_APPLICATION_FUNDING_TYPES,
  IRA_TRANSFER_TYPES,
} from '../../util/constants';
import { parseEnumType } from '../../util/helpers/enumMappers';
import EditIcon from '../Icons/Edit';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import themeColors from '../../assets/styles/themeColors';

const FundingReviewContainer = styled.div`
  padding: 1.5rem;
  background-color: ${themeColors.colorBackgroundSecondary};
  margin-bottom: ${(props) => (props.mb ? `${props.mb}px` : 0)};
  margin-top: ${(props) => (props.mt ? `${props.mt}px` : 0)};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 1rem;
`;

const FundingReviewContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 4px;
`;

const EditIconWrap = styled(IconButton)`
  color: ${themeColors.colorTextSecondary};
`;

const IraFundingReview = ({
  funding,
  mb,
  mt,
  onEdit,
  missingInformation,
  dataCy,
}) => {
  const { t } = useTranslation();

  if (isEmpty(funding)) {
    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
            No funding instructions added.
          </Paragraph>
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit} data-cy={dataCy}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  if (missingInformation) {
    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
            Application funding type is defined, but amount is missing.
          </Paragraph>
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit} data-cy={dataCy}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  if (
    parseEnumType(IRA_APPLICATION_FUNDING_TYPES, funding?.IRAFundingType) ===
    parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 1)
  ) {
    // eslint-disable-next-line no-unused-vars
    const {
      TransferType,
      IRAFundingCustodianResponse,
      CustodianAccountType,
    } = funding;

    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph fontWeight={700}>
            {t('iraCustodian.review.iraReview', {
              amount: formatMoneyNumeral(funding?.Amount),
              transferType: parseEnumType(IRA_TRANSFER_TYPES, TransferType),
              iraType: parseEnumType(IRA_ACCOUNT_TYPES, CustodianAccountType),
              accountNumber: IRAFundingCustodianResponse?.AccountNumber?.substring(
                IRAFundingCustodianResponse?.AccountNumber.lengths - 4,
              ),
              custodianName: `${IRAFundingCustodianResponse?.Name}`,
            })}
          </Paragraph>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight} />
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit} data-cy={dataCy}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  return (
    <FundingReviewContainer mb={mb} mt={mt}>
      <FundingReviewContent>
        <Paragraph fontWeight={700}>
          {formatMoneyNumeral(funding?.Amount)}{' '}
          {t(
            `standardReview.fundTypes.${parseEnumType(
              IRA_APPLICATION_FUNDING_TYPES,
              funding?.IRAFundingType,
            )}`,
          )}
        </Paragraph>
        <Paragraph variant="normal" color={themeColors.colorTextXLLight} />
      </FundingReviewContent>
      <EditIconWrap onClick={onEdit} data-cy={dataCy}>
        <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
      </EditIconWrap>
    </FundingReviewContainer>
  );
};

IraFundingReview.propTypes = {
  mb: PropTypes.number,
  mt: PropTypes.number,
  onEdit: PropTypes.func,
  funding: PropTypes.shape({
    IRAFundingType: PropTypes.number,
    Amount: PropTypes.number,
    TransferType: PropTypes.number,
    IRAFundingCustodianResponse: PropTypes.shape({
      Name: PropTypes.string,
      AccountNumber: PropTypes.string,
    }),
    CustodianAccountType: PropTypes.number,
  }),
  missingInformation: PropTypes.bool,
  dataCy: PropTypes.string,
};

export default IraFundingReview;
